import { useEffect, useState } from "react";
import type {
  AreaPractica,
  Cliente,
  Expediente,
  Fuero,
  Materia,
  Organismo,
  ProcesoTrabajo,
  Secretaria,
  Submateria,
} from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { Campo, Input, Select, TextArea } from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { useCambiosSinGuardar } from "../../hooks/useCambiosSinGuardar";
import { api } from "../../lib/apiClient";

const PRIORIDADES = ["Alta", "Media", "Baja"] as const;

export function FormularioExpediente({
  abierto,
  clientes,
  guardando,
  valoresIniciales,
  onCerrar,
  onGuardar,
}: {
  abierto: boolean;
  clientes: Cliente[];
  guardando: boolean;
  valoresIniciales?: Partial<Expediente>;
  onCerrar: () => void;
  onGuardar: (valores: Record<string, unknown>) => void;
}) {
  const [valores, setValores] = useState<Record<string, string>>({});
  // Snapshot de los valores justo al abrir — comparar contra esto (no
  // contra `valoresIniciales`, que es una prop que puede no cambiar de
  // referencia) es lo que detecta "cambios reales" para el aviso de salir
  // sin guardar.
  const [valoresBase, setValoresBase] = useState<Record<string, string>>({});
  const [materias, setMaterias] = useState<Materia[]>([]);
  const [fueros, setFueros] = useState<Fuero[]>([]);
  const [submaterias, setSubmaterias] = useState<Submateria[]>([]);
  const [organismos, setOrganismos] = useState<Organismo[]>([]);
  const [secretarias, setSecretarias] = useState<Secretaria[]>([]);
  const [areasPractica, setAreasPractica] = useState<AreaPractica[]>([]);
  const [procesos, setProcesos] = useState<ProcesoTrabajo[]>([]);

  useEffect(() => {
    if (!abierto) return;
    api.get<{ items: Materia[] }>("/catalogos/materias").then((d) => setMaterias(d.items));
    api.get<{ items: Fuero[] }>("/catalogos/fueros").then((d) => setFueros(d.items));
    api
      .get<{ items: AreaPractica[] }>("/catalogos-estudio/areas-practica")
      .then((d) => setAreasPractica(d.items.filter((a) => a.activo)))
      .catch(() => undefined);
    api
      .get<{ items: ProcesoTrabajo[] }>("/procesos")
      .then((d) => setProcesos(d.items.filter((p) => p.activo)))
      .catch(() => undefined);
  }, [abierto]);

  useEffect(() => {
    if (abierto) {
      const iniciales = {
        numero_expediente: valoresIniciales?.numero_expediente ?? "",
        cliente_id: valoresIniciales?.cliente_id ?? "",
        materia_id: valoresIniciales?.materia_id ?? "",
        submateria_id: valoresIniciales?.submateria_id ?? "",
        fuero_id: valoresIniciales?.fuero_id ?? "",
        organismo_id: valoresIniciales?.organismo_id ?? "",
        secretaria_id: valoresIniciales?.secretaria_id ?? "",
        area_practica_id: valoresIniciales?.area_practica_id ?? "",
        proceso_id: valoresIniciales?.proceso_id ?? "",
        rol_cliente: valoresIniciales?.rol_cliente ?? "",
        fecha_apertura:
          valoresIniciales?.fecha_apertura ??
          new Date().toISOString().substring(0, 10),
        prioridad: valoresIniciales?.prioridad ?? "Media",
        tipo_reclamo: valoresIniciales?.tipo_reclamo ?? "",
        monto_reclamado_estimado:
          valoresIniciales?.monto_reclamado_estimado?.toString() ?? "",
        observaciones: valoresIniciales?.observaciones ?? "",
      };
      setValores(iniciales);
      setValoresBase(iniciales);
    }
  }, [abierto, valoresIniciales]);

  const hayCambios = abierto && JSON.stringify(valores) !== JSON.stringify(valoresBase);
  const { intentarSalir, mostrandoConfirmacion, confirmarSalida, seguirEditando } = useCambiosSinGuardar(hayCambios);

  function cerrar() {
    intentarSalir(onCerrar);
  }

  // Al abrir para editar un expediente existente, precarga las submaterias
  // de su materia actual (si no, el select de Submateria quedaría vacío
  // hasta que el usuario tocara el de Materia).
  useEffect(() => {
    const materiaId = valores.materia_id;
    if (!materiaId) {
      setSubmaterias([]);
      return;
    }
    api
      .get<{ items: Submateria[] }>(`/catalogos/materias/${materiaId}/submaterias`)
      .then((d) => setSubmaterias(d.items));
  }, [valores.materia_id]);

  // Organismo depende del Fuero (no de Materia/Submateria) — misma lógica
  // de precarga que Submateria, para que al editar un expediente existente
  // el select ya aparezca poblado.
  useEffect(() => {
    const fueroId = valores.fuero_id;
    if (!fueroId) {
      setOrganismos([]);
      return;
    }
    api
      .get<{ items: Organismo[] }>(`/catalogos/fueros/${fueroId}/organismos`)
      .then((d) => setOrganismos(d.items));
  }, [valores.fuero_id]);

  useEffect(() => {
    const organismoId = valores.organismo_id;
    if (!organismoId) {
      setSecretarias([]);
      return;
    }
    api
      .get<{ items: Secretaria[] }>(`/catalogos/organismos/${organismoId}/secretarias`)
      .then((d) => setSecretarias(d.items));
  }, [valores.organismo_id]);

  function actualizar(campo: string, valor: string) {
    setValores((v) => ({ ...v, [campo]: valor }));
  }

  function cambiarMateria(materiaId: string) {
    // Al cambiar la Materia se resetea la Submateria: no debe quedar
    // seleccionada una Submateria de otra Materia.
    setValores((v) => ({ ...v, materia_id: materiaId, submateria_id: "" }));
  }

  function cambiarFuero(fueroId: string) {
    // Al cambiar el Fuero se resetean Organismo y Secretaría: ninguno de
    // los dos puede seguir apuntando a datos de otro Fuero.
    setValores((v) => ({ ...v, fuero_id: fueroId, organismo_id: "", secretaria_id: "" }));
  }

  function cambiarOrganismo(organismoId: string) {
    // Al cambiar el Organismo se resetea la Secretaría.
    setValores((v) => ({ ...v, organismo_id: organismoId, secretaria_id: "" }));
  }

  function manejarEnvio(e: React.FormEvent) {
    e.preventDefault();
    onGuardar({
      ...valores,
      // Organismo/Secretaría son opcionales — el backend distingue "no
      // elegido" de "elegido" por null/undefined, nunca por string vacío.
      organismo_id: valores.organismo_id || null,
      secretaria_id: valores.secretaria_id || null,
      area_practica_id: valores.area_practica_id || null,
      proceso_id: valores.proceso_id || null,
      monto_reclamado_estimado: valores.monto_reclamado_estimado
        ? Number(valores.monto_reclamado_estimado)
        : null,
    });
  }

  return (
    <Modal
      titulo={valoresIniciales ? "Editar expediente" : "Nuevo expediente"}
      abierto={abierto}
      onCerrar={cerrar}
      ancho="max-w-xl"
    >
      <form onSubmit={manejarEnvio} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo etiqueta="Número de expediente" obligatorio>
            <Input
              required
              value={valores.numero_expediente ?? ""}
              onChange={(e) => actualizar("numero_expediente", e.target.value)}
              placeholder="EXP-2026-0001"
            />
          </Campo>
          <Campo etiqueta="Cliente" obligatorio>
            <Select
              required
              value={valores.cliente_id ?? ""}
              onChange={(e) => actualizar("cliente_id", e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre_completo}
                </option>
              ))}
            </Select>
          </Campo>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Campo etiqueta="Materia" obligatorio>
            <Select
              required
              value={valores.materia_id ?? ""}
              onChange={(e) => cambiarMateria(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {materias.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Submateria" obligatorio>
            <Select
              required
              disabled={!valores.materia_id}
              value={valores.submateria_id ?? ""}
              onChange={(e) => actualizar("submateria_id", e.target.value)}
            >
              <option value="">
                {valores.materia_id ? "Seleccionar..." : "Elegí una materia primero"}
              </option>
              {submaterias.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Fuero" obligatorio>
            <Select
              required
              value={valores.fuero_id ?? ""}
              onChange={(e) => cambiarFuero(e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {fueros.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nombre}
                </option>
              ))}
            </Select>
          </Campo>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Campo etiqueta="Organismo / Tribunal">
            <Select
              disabled={!valores.fuero_id}
              value={valores.organismo_id ?? ""}
              onChange={(e) => cambiarOrganismo(e.target.value)}
            >
              <option value="">
                {valores.fuero_id ? "Sin especificar" : "Elegí un fuero primero"}
              </option>
              {organismos.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nombre}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Secretaría / Sala">
            <Select
              disabled={!valores.organismo_id}
              value={valores.secretaria_id ?? ""}
              onChange={(e) => actualizar("secretaria_id", e.target.value)}
            >
              <option value="">
                {valores.organismo_id ? "Sin especificar" : "Elegí un organismo primero"}
              </option>
              {secretarias.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </Select>
          </Campo>
        </div>
        {areasPractica.length > 0 && (
          <Campo etiqueta="Unidad de Negocio" obligatorio>
            <Select
              required
              value={valores.area_practica_id ?? ""}
              onChange={(e) => actualizar("area_practica_id", e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {areasPractica.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </Select>
          </Campo>
        )}
        {procesos.length > 0 && (
          <Campo etiqueta="Proceso de Trabajo">
            <Select
              value={valores.proceso_id ?? ""}
              onChange={(e) => actualizar("proceso_id", e.target.value)}
            >
              <option value="">Sin proceso asignado</option>
              {procesos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </Campo>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Campo etiqueta="Rol del cliente">
            <Input
              value={valores.rol_cliente ?? ""}
              onChange={(e) => actualizar("rol_cliente", e.target.value)}
              placeholder="Actor / Demandado"
            />
          </Campo>
          <Campo etiqueta="Fecha de apertura" obligatorio>
            <Input
              type="date"
              required
              value={valores.fecha_apertura ?? ""}
              onChange={(e) => actualizar("fecha_apertura", e.target.value)}
            />
          </Campo>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Campo etiqueta="Prioridad" obligatorio>
            <Select
              value={valores.prioridad ?? ""}
              onChange={(e) => actualizar("prioridad", e.target.value)}
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Monto reclamado estimado">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={valores.monto_reclamado_estimado ?? ""}
              onChange={(e) =>
                actualizar("monto_reclamado_estimado", e.target.value)
              }
            />
          </Campo>
        </div>
        <Campo etiqueta="Tipo de reclamo">
          <Input
            value={valores.tipo_reclamo ?? ""}
            onChange={(e) => actualizar("tipo_reclamo", e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Observaciones">
          <TextArea
            rows={3}
            value={valores.observaciones ?? ""}
            onChange={(e) => actualizar("observaciones", e.target.value)}
          />
        </Campo>
        <div className="flex justify-end gap-2 pt-2">
          <Boton
            type="button"
            variante="secundario"
            onClick={cerrar}
            disabled={guardando}
          >
            Cancelar
          </Boton>
          <Boton type="submit" cargando={guardando}>
            Guardar
          </Boton>
        </div>
      </form>

      <ConfirmacionDialog
        abierto={mostrandoConfirmacion}
        titulo="Cambios sin guardar"
        mensaje="Tienes cambios sin guardar. ¿Deseas salir sin guardarlos?"
        onConfirmar={confirmarSalida}
        onCancelar={seguirEditando}
        textoCancelar="Seguir editando"
        textoConfirmar="Salir sin guardar"
        peligroso
      />
    </Modal>
  );
}
